import React, { useCallback, useId, useState } from 'react'
import { DateRangePicker } from 'rsuite'
import DateFormat from '../../lib.r2/utils/DateFormat'

export type DatePickerValue = {
	startDate: string | Date
	endDate: string | Date
}

export type DatePickerProps = {
	id?: string
	'aria-label'?: string
	value?: DatePickerValue
	onChange?: (value: DatePickerValue | null) => void
	newOnly?: boolean // deprecated, use allowPast instead
	allowPast?: boolean // if false, disables past dates
	className?: string
	'data-pw'?: string
}

export const DatePicker: React.FC<DatePickerProps> = ({
	id,
	'aria-label': ariaLabel = 'Zeitraum wählen',
	value,
	onChange,
	newOnly,
	allowPast = false,
	className,
	'data-pw': dataPw,
}) => {
	const generatedId = useId().replace(/[^a-z0-9_-]+/gi, '-')
	const inputId = id ?? `date-picker-${generatedId}`

	const [selectedValue, setSelectedValue] = useState<[Date, Date] | null>(
		value?.startDate && value?.endDate
			? [new Date(value.startDate), new Date(value.endDate)]
			: null
	)

	const onSelectDates = useCallback(
		(dates: Date[]) => {
			console.log('selected dates:', dates)
			if (dates.length === 2) {
				setSelectedValue([dates[0], dates[1]])
			}
			onChange?.({
				startDate: DateFormat(dates[0], 'yyyy-mm-dd'),
				endDate: DateFormat(dates[1], 'yyyy-mm-dd'),
			})
		},
		[onChange]
	)

	const onClean = useCallback(() => {
		setSelectedValue(null)
		onChange?.(null)
	}, [onChange])

	return (
		<div className="date-picker-field">
			<DateRangePicker
				ref={e => {
					if (e?.target instanceof HTMLInputElement) {
						e.target.id = inputId
						e.target.setAttribute('aria-label', ariaLabel)
						e.target.setAttribute('data-pw', dataPw ?? 'date-range-picker-input')
					}
				}}
				className={"datepicker " + (className ?? '')}
				value={selectedValue}
				weekStart={1}
				limitStartYear={2025}
				limitEndYear={2030}
				shouldDisableDate={
					allowPast ? undefined : DateRangePicker.beforeToday()
				}
				onOk={onSelectDates}
				onClean={onClean}
				character=" - "
				ranges={[]}
				placeholder="TT.MM.JJJJ - TT.MM.JJJJ"
				editable={false}
				// German localization for explanation and tooltips
				locale={{
					ok: 'OK',
					today: 'Heute',
					yesterday: 'Gestern',
					last7Days: 'Letzte 7 Tage',
				}}
				// German explanation for the user
				renderValue={(value) => {
					// If value is not set, show German placeholder for both dates
					if (!value || value.length !== 2 || !value[0] || !value[1]) {
						return 'TT.MM.JJJJ - TT.MM.JJJJ';
					}
					const [start, end] = value;
					const format = (date: Date) => date ? `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}` : 'TT.MM.JJJJ';
					return `${format(start)} - ${format(end)}`;
				}}
				// renderExtraFooter removed to avoid React warning
			/>
		</div>
	)
}
